import { Alert } from '@ksyos/design-system';
import type { IPatient } from '@common/interfaces/patient';
import type { AssetValidation } from '@common/validation/serviceOrganizationSetting';
import { CancelButton } from '@frontend/components/elements/carePathButtons/CancelButton';
import { getAssetValidationSettings } from '@frontend/services/api/serviceOrganizationSettingApiService';
import { useEpisodeContext } from '@frontend/utils';
import { renameErrorKeys } from '@frontend/utils/renameErrorKeys';
import type { IOphthalmologyAsset } from '../../../../../../common/validation/customControls/ophthalmology';
import type { ICustomControlProcedureAddEditProps } from '../../interfaces/customControlProcedureAddEditProps';
import type { ServiceConsumerProp } from '../../utils/v1/loadServiceConsumer';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getSelectedLanguage } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import {
    ButtonContainer,
    HeaderDivider,
    InputRadioGroup,
    InputTextArea,
    SmallNullableInputText,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { ProceedWithOnlyAdditionalFiles } from '../../../../elements/ProceedWithOnlyAdditionalFiles';
import { ProceedWithoutFiles } from '../../../../elements/ProceedWithoutFiles';
import { CareProviderSelector } from '../../utils/v1/CareProviderSelector';
import { GraderSelector } from '../../utils/v1/GraderSelector';
import { useLoadServiceConsumer } from '../../utils/v1/loadServiceConsumer';
import { NewOrEditProcedureProcessContainer } from '../../utils/v1/NewOrEditProcedureProcessContainer';
import { ServiceOrganizationConsumerSelector } from '../../utils/v1/ServiceOrganizationConsumerSelector';
import { OphthalmologyDropzoneProcedureAddEdit } from './OphthalmologyDropzoneProcedureAddEdit';

interface IInnerTfoV1ProcedureAddEditState {
    assetValidation?: AssetValidation;
    buttonDisabled?: boolean;
    proceedWithOnlyAdditionalFiles: boolean;
    proceedWithoutFiles: boolean;
    showProceedWithOnlyAdditionalFiles?: boolean;
    showProceedWithoutFiles?: boolean;
}

export interface IProcedureUploadingFiles {
    assets: IOphthalmologyAsset[];
    onUploadingFiles: (isUploading: boolean) => void | Promise<void>;
}

export function TfoV1ProcedureAddEdit(props: ICustomControlProcedureAddEditProps & IProcedureUploadingFiles) {
    const serviceConsumer = useLoadServiceConsumer(props.service.id, {
        organizationId: props.procedure.requesterOrganizationId!,
        practitionerId: props.procedure.requesterPractitionerId!,
    });
    const { patient } = useEpisodeContext();

    return (
        <InnerTfoV1ProcedureAddEdit
            {...props}
            patient={patient}
            serviceConsumer={serviceConsumer}
        />
    );
}

class InnerTfoV1ProcedureAddEdit extends Component<
    ICustomControlProcedureAddEditProps & ServiceConsumerProp & IProcedureUploadingFiles & { patient: IPatient },
    IInnerTfoV1ProcedureAddEditState
> {
    public async componentDidMount() {
        const assetValidation = await getAssetValidationSettings(this.props.organizationId);
        this.setState({ assetValidation });
    }

    public render() {
        const translate = getSelectedLanguage();

        return (
            <NewOrEditProcedureProcessContainer
                currentProcedure={this.props.currentProcedure}
                isBioGrader={this.props.isBioGrader}
                isFirstItem={this.props.isFirstItem}
                itemState={this.props.itemState}
                process={this.props.process}
                service={this.props.service}
            >
                <CareProviderSelector
                    {...this.props}
                    entity={this.props.procedure}
                    errors={renameErrorKeys(this.props.errors, 'customControlData.', '')}
                    skipValidationOnChange={true}
                />

                <ServiceOrganizationConsumerSelector {...this.props} />

                <HeaderDivider text={translate.diagnosticReport.examination} />

                {this.isBsnAssetValidationEnabled() && (
                    <>
                        {this.props.patient.identifierSocialSecurity ? (
                            <Alert
                                appearance="info"
                                hasIcon={true}
                            >
                                {translate.ophthalmology.bsnCheckMatchingMessage}
                            </Alert>
                        ) : (
                            <Alert
                                appearance="warning"
                                hasIcon={true}
                            >
                                {translate.ophthalmology.unknownBsnMessage}
                            </Alert>
                        )}
                    </>
                )}

                {this.addEditForm()}

                {!this.props.isBioGrader && (
                    <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                        <FormLabel>{translate.diagnosticReport.remarks}</FormLabel>
                        <InputContainer>
                            <InputTextArea
                                automation="remark"
                                error={this.props.errors.get('remark')}
                                onChange={async (value) => await this.props.handleChange('remark', value)}
                                onDoValidate={async () => await this.props.validateOne('remark')}
                                placeholder={translate.common.remarksOrQuestions}
                                value={this.props.procedure.remark}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}

                <GraderSelector
                    {...this.props}
                    careProviderAndRequesterAndSelectedProvider={{ ...this.props.procedure }}
                    procedurePerformerOrganizationId={this.props.organizationId}
                />

                <ButtonContainer
                    alignRight
                    buttons={[
                        <CancelButton
                            cancel={this.props.onCancel}
                            entity={ProcessEntity.procedure}
                            isExisting={!!this.props.request.id}
                            revertChanges={this.props.revertProcedureChanges}
                            serviceCode={this.props.service.code}
                            serviceType={this.props.service.serviceType}
                        />,
                        <SaveButton
                            buttonDisabled={this.state.buttonDisabled}
                            save={async () => {
                                await this.saveProcedure('draft');
                            }}
                        />,
                        <SaveSendButton
                            buttonDisabled={this.state.buttonDisabled}
                            saveSend={async () => {
                                await this.saveProcedure('final');
                            }}
                        />,
                    ]}
                    hideButtons={this.props.isBioGrader}
                />
                {this.state.showProceedWithoutFiles && (
                    <ProceedWithoutFiles
                        entity={ProcessEntity.procedure}
                        onNo={async () =>
                            await this.setState({ showProceedWithoutFiles: false, buttonDisabled: false })
                        }
                        onYes={async () => {
                            await this.setState({ showProceedWithoutFiles: false, proceedWithoutFiles: true });
                            await this.saveProcedure('final');
                        }}
                        serviceType={this.props.service.serviceType}
                    />
                )}
                {this.state.showProceedWithOnlyAdditionalFiles && (
                    <ProceedWithOnlyAdditionalFiles
                        onNo={async () =>
                            await this.setState({
                                showProceedWithOnlyAdditionalFiles: false,
                                buttonDisabled: false,
                            })
                        }
                        onYes={async () => {
                            await this.setState({
                                showProceedWithOnlyAdditionalFiles: false,
                                proceedWithOnlyAdditionalFiles: true,
                            });
                            await this.saveProcedure('final');
                        }}
                    />
                )}
            </NewOrEditProcedureProcessContainer>
        );
    }

    public addEditForm() {
        return (
            <>
                <OphthalmologyDropzoneProcedureAddEdit
                    assets={this.props.assets ?? this.props.procedure.customControlData.files}
                    handleChangeCustomControl={this.props.handleChangeCustomControl}
                    isAutoSaveActive={this.props.isAutoSaveActive}
                    isBsnValidationEnabled={
                        this.isBsnAssetValidationEnabled() && !!this.props.patient.identifierSocialSecurity
                    }
                    onUploading={async (isUploading) => {
                        if (this.props.isBioGrader && this.props.onUploadingFiles) {
                            await this.props.onUploadingFiles(isUploading);
                        } else {
                            await this.setState({ buttonDisabled: isUploading });
                        }
                    }}
                    patientBsn={this.props.patient.identifierSocialSecurity ?? undefined}
                    procedure={this.props.procedure}
                    service={this.props.service}
                />

                <HorizontalFormGroup>
                    <Alert appearance="info">Let op: voeg altijd een fundusonderzoek toe!</Alert>
                </HorizontalFormGroup>

                <HorizontalFormGroup>
                    <FormLabel>{''}</FormLabel>
                    <InputContainer>
                        <FormLabel>OD</FormLabel>
                    </InputContainer>
                    <InputContainer>
                        <FormLabel>OS</FormLabel>
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.visus_od') ||
                        this.props.errors.has('customControlData.visus_os')
                    }
                >
                    <FormLabel>Visus</FormLabel>
                    <InputContainer>
                        <SmallNullableInputText
                            automation="visusOd"
                            error={this.props.errors.get('customControlData.visus_od')}
                            onChange={async (value) => {
                                await this.props.handleChangeCustomControl({ visus_od: value });
                                if (!value && !this.props.procedure.customControlData.visus_os) {
                                    await this.props.handleChangeCustomControl({ hoe_gemeten: null });
                                }
                            }}
                            onDoValidate={async () => await this.props.validateOneCustomControl('visus_od')}
                            value={this.props.procedure.customControlData.visus_od}
                        />
                    </InputContainer>
                    <InputContainer>
                        <SmallNullableInputText
                            automation="visusOs"
                            error={this.props.errors.get('customControlData.visus_os')}
                            onChange={async (value) => {
                                await this.props.handleChangeCustomControl({ visus_os: value });
                                if (!value && !this.props.procedure.customControlData.visus_od) {
                                    await this.props.handleChangeCustomControl({ hoe_gemeten: null });
                                }
                            }}
                            onDoValidate={async () => await this.props.validateOneCustomControl('visus_os')}
                            value={this.props.procedure.customControlData.visus_os}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                {(this.props.procedure.customControlData.visus_od ||
                    this.props.procedure.customControlData.visus_os) && (
                    <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.hoe_gemeten')}>
                        <FormLabel isRequired>Hoe gemeten</FormLabel>
                        <InputContainer>
                            <InputRadioGroup
                                automation="howMeasured"
                                error={this.props.errors.get('customControlData.hoe_gemeten')}
                                name="hoe_gemeten"
                                onChange={async (value) =>
                                    await this.props.handleChangeCustomControl({ hoe_gemeten: value })
                                }
                                onDoValidate={async () => await this.props.validateOneCustomControl('hoe_gemeten')}
                                options={[
                                    'Visus SC (zonder correctie)',
                                    'Visus CC (met correctie)',
                                    'Visus BCVA (optimale correctie)',
                                    'Visus EC (eigen correctie)',
                                ]}
                                radioButtonsStacked
                                value={this.props.procedure.customControlData.hoe_gemeten}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}

                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.oogdruk_od') ||
                        this.props.errors.has('customControlData.oogdruk_os')
                    }
                >
                    <FormLabel>Oogdruk</FormLabel>
                    <InputContainer>
                        <SmallNullableInputText
                            automation="eyePressureOd"
                            error={this.props.errors.get('customControlData.oogdruk_od')}
                            onChange={async (value) =>
                                await this.props.handleChangeCustomControl({ oogdruk_od: value })
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('oogdruk_od')}
                            value={this.props.procedure.customControlData.oogdruk_od}
                        />
                    </InputContainer>
                    <InputContainer>
                        <SmallNullableInputText
                            automation="eyePressureOs"
                            error={this.props.errors.get('customControlData.oogdruk_os')}
                            onChange={async (value) =>
                                await this.props.handleChangeCustomControl({ oogdruk_os: value })
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('oogdruk_os')}
                            value={this.props.procedure.customControlData.oogdruk_os}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.patient_gedruppeld')}>
                    <FormLabel isRequired>Patiënt gedruppeld</FormLabel>
                    <InputContainer>
                        <InputRadioGroup
                            automation="patientEyeDrops"
                            error={this.props.errors.get('customControlData.patient_gedruppeld')}
                            name="patient_gedruppeld"
                            onChange={async (value) =>
                                await this.props.handleChangeCustomControl({ patient_gedruppeld: value })
                            }
                            onDoValidate={async () => await this.props.validateOneCustomControl('patient_gedruppeld')}
                            options={['Ja', 'Nee']}
                            value={this.props.procedure.customControlData.patient_gedruppeld}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            </>
        );
    }

    private async saveProcedure(saveType: 'draft' | 'final') {
        await this.setState({ buttonDisabled: true });

        const validatedProcedure = await this.props.validateProcedure(saveType);
        if (!validatedProcedure) {
            await this.setState({ buttonDisabled: false });

            return;
        }

        if (saveType === 'final') {
            if (
                !this.state.proceedWithoutFiles &&
                (!this.props.procedure.customControlData.files ||
                    this.props.procedure.customControlData.files.length === 0)
            ) {
                return await this.setState({ showProceedWithoutFiles: true });
            } else if (
                !this.state.proceedWithOnlyAdditionalFiles &&
                this.props.procedure.customControlData.files.length > 0 &&
                this.props.procedure.customControlData.files.every(
                    (file: IOphthalmologyAsset) => file.placement === 'aanvullend'
                )
            ) {
                return await this.setState({ showProceedWithOnlyAdditionalFiles: true });
            }
        }

        const requestId = await this.props.onSaveProcedure(saveType);
        await this.setState({ buttonDisabled: false });
        if (!requestId) {
            return;
        }

        if (saveType === 'final') {
            showServiceToast(
                ToastType.success,
                'toast.procedureSubmitted',
                this.props.service,
                'procedure',
                getSelectedLanguage().toast.submitDefaultMessage
            );
        } else {
            showServiceToast(
                ToastType.success,
                'toast.draftSuccess',
                this.props.service,
                'procedure',
                getSelectedLanguage().toast.draftDefaultMessage
            );
        }

        await this.props.onSave(requestId);
    }

    private isBsnAssetValidationEnabled() {
        return this.state.assetValidation?.isEnabled && this.state.assetValidation?.type === 'BSN';
    }
}

export function tfoV1ProcedureInitializeCustomControlData() {
    return {
        visus_od: null,
        visus_os: null,
        hoe_gemeten: null,
        oogdruk_od: null,
        oogdruk_os: null,
        patient_gedruppeld: null,
        files: [],
    };
}
